import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { KendoBaseComponent } from '../kendo-base/kendo-base.component';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { LabelView } from '@app/core/services/core-component.service';
import { TranslateService } from '@app/core/services/translate.service';
import { EditorPasteEvent, EditorComponent as KendoEditorComponent } from '@progress/kendo-angular-editor';
import { SirvService } from '@app/services/sirv.service';
import { LayoutService } from '@app/core/services/layout.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
    selector: 'tt-editor',
    templateUrl: './editor.component.html',
    styleUrls: ['./editor.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditorComponent extends KendoBaseComponent implements OnChanges {
    schema = mySchema;

    /**
     * The label to display for the form-field. Always required, use label-view to hide a label.
     */
    @Input()
    public ttLabel: string = '';

    /**
     * The label connected to the template.
     */
    public label: string = '';

    public dynamicScopedStyles!: SafeHtml;

    @Input()
    ttEditorCss = '';

    /**
     * The view of the label.
     */
    @Input()
    public ttLabelView: LabelView = 'auto';

    /**
     * Whether or not the main label of the form-field should be translated.
     *
     * @default true
     */
    @Input()
    public get ttTranslateLabel(): boolean {
        return this._translateLabel;
    }
    public set ttTranslateLabel(value: BooleanInput) {
        this._translateLabel = coerceBooleanProperty(value);
    }
    private _translateLabel: boolean = true;

    /**
     * Secondary label for the form-field, typically helper text.
     */
    @Input()
    public ttSublabel: string = '';

    /**
     * The sublabel displayed in template.
     */
    public sublabel: string = '';

    /**
     * Whether or not the sub-label of the form-field should be translated.
     *
     * @default false
     */
    @Input()
    public get ttTranslateSublabel(): boolean {
        return this._translateSublabel;
    }
    public set ttTranslateSublabel(value: BooleanInput) {
        this._translateSublabel = coerceBooleanProperty(value);
    }
    private _translateSublabel: boolean = false;

    /**
     * Whether value in the editor is required or not, displays a red star in label if it is.
     */
    @Input()
    public get ttRequired(): boolean {
        return this._required;
    }
    public set ttRequired(value: BooleanInput) {
        this._required = coerceBooleanProperty(value);
    }
    private _required: boolean = false;

    /**
     * The html content to render and be edited in the editor.
     */
    @Input()
    public ttModel: string = '';

    /**
     * Event emitted when the content inside the editor has changed.
     */
    @Output()
    public ttModelChange = new EventEmitter<string>();

    /**
     * Whether the editor can be edited or not.
     *
     * @default false
     */
    @Input()
    public get ttReadonly(): boolean {
        return this._readonly;
    }
    public set ttReadonly(value: BooleanInput) {
        this._readonly = coerceBooleanProperty(value);
    }
    private _readonly: boolean = false;

    /**
     * The height of the editor, with 'auto' it will grow to the content of the editor.
     */
    @Input()
    public ttHeight: 'auto' | `${number}px` | `${number}vh` | `${number}rem` | `${number}%` = 'auto';

    /**
     * **reactive** - If sirv is configured this flag changes when an image is being uploaded to sirv.
     */
    @Input()
    public ttProcessing: boolean = false;

    /**
     * Event emitted when the value of processing has changed.
     */
    @Output()
    public ttProcessingChange = new EventEmitter<boolean>();

    @ViewChild('editor')
    public editor?: KendoEditorComponent;

    public mobileQuery: MediaQueryList;

    constructor(
        private translate: TranslateService,
        private sirv: SirvService,
        private layout: LayoutService,
        private sanitizer: DomSanitizer,
    ) {
        super();
        this.mobileQuery = layout.getMediaQueries().tablet;
    }

    /**
     * Handles value changes in editor.
     *
     * @param _ the new html value in the editor.
     */
    public onValueChange(_: string) {
        this.ttModelChange.emit(this.ttModel);
    }

    /**
     * Handles when processing value changed.
     *
     * @param value the new value of the processing flag.
     */
    public onProcessingChanged(value: boolean) {
        this.ttProcessing = value;
        this.ttProcessingChange.emit(this.ttProcessing);
    }

    /**
     * Handles pasting of images in editor.
     *
     * @param event the paste events of the editor.
     */
    public async onPaste(event: EditorPasteEvent | ClipboardEvent) {
        if (event instanceof EditorPasteEvent) {
            const pastedBase64Images = this.getBase64ImageStringsFromHtml(event.cleanedHtml);

            if (pastedBase64Images.length > 0 && (await this.sirv.checkConnection()) === true) {
                this.onProcessingChanged(true);

                try {
                    await Promise.all(pastedBase64Images.map(this.convertBase64StringToFileThenUploadAndReplaceWithLink));

                    this.onValueChange(this.ttModel);
                } finally {
                    this.onProcessingChanged(false);
                }
            }
        }
    }

    public onCssChanged() {
        const scopedCss = this.scopeCssString(this.ttEditorCss, `.tt-editor .ProseMirror`);
        this.dynamicScopedStyles = this.sanitizer.bypassSecurityTrustHtml(`<style>${scopedCss}</style>`);
    }

    private scopeCssString(css: string, prefix: string): string {
        return css
            .split('}')
            .map((rule) => {
                const parts = rule.split('{');
                if (parts.length !== 2) return rule;

                const selectors = parts[0]
                    .split(',')
                    .map((s) => `${prefix} ${s.trim()}`)
                    .join(', ');

                return `${selectors} { ${parts[1]} }`;
            })
            .join('\n');
    }

    private convertBase64StringToFileThenUploadAndReplaceWithLink = async (base64Image: string) => {
        const file = await this.base64ImageStringToFile(base64Image);

        if (!!file) {
            await this.uploadFileAndReplaceBase64StringWithImageLink(file, base64Image);
        }
    };

    private async uploadFileAndReplaceBase64StringWithImageLink(file: File, base64Image: string) {
        const response = await this.sirv.uploadImage(file, 'html');

        this.ttModel = this.ttModel.replace(`${base64Image}`, `${response.imageUrl}?w=800&scale.option=noup" style="max-width: calc(100vw - 50px)"`);
    }

    /**
     * Converts the given base64 encoded image string into a file. Returns the newly created image file, if a file could not be created return `null`.
     *
     * @param base64Image the base64 encoded string to convert into a file.
     * @returns a promise containing the newly create file or `null` if a file could not be created.
     */
    private async base64ImageStringToFile(base64Image: string): Promise<File | null> {
        try {
            const response = await fetch(base64Image);

            if (response.ok) {
                const blob = await response.blob();
                return new File([blob], `clipboard.${blob.type.split('/')[1]}`, { type: blob.type });
            } else {
                return null;
            }
        } catch (error) {
            return null;
        }
    }

    private getBase64ImageStringsFromHtml(html: string) {
        return html.split('"').filter((string) => this.isBase64EncodedImageString(string));
    }

    private isBase64EncodedImageString(value: string): boolean {
        return !!value && typeof value === 'string' && value.includes('data:image/') && value.includes('base64');
    }

    private async setLabel(label: string) {
        if (this.ttTranslateLabel) {
            this.label = await this.translate.translate(label);
        } else {
            this.label = this.ttLabel;
        }
    }

    private async setSublabel(sublabel: string) {
        if (this.ttTranslateSublabel) {
            this.sublabel = await this.translate.translate(sublabel);
        } else {
            this.sublabel = this.ttSublabel;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ttTranslateLabel']) {
            this.ttTranslateLabel = changes['ttTranslateLabel'].currentValue;
        }

        if (changes['ttTranslateSublabel']) {
            this.ttTranslateSublabel = changes['ttTranslateSublabel'].currentValue;
        }

        if (changes['ttRequired']) {
            this.ttRequired = changes['ttRequired'].currentValue;
        }

        if (changes['ttReadonly']) {
            this.ttReadonly = changes['ttReadonly'].currentValue;
        }

        if (!!changes['ttLabel']?.currentValue && typeof changes['ttLabel'].currentValue === 'string' && changes['ttLabel'].currentValue != changes['ttLabel'].previousValue) {
            this.setLabel(changes['ttLabel'].currentValue);
        }

        if (!!changes['ttSublabel']?.currentValue && typeof changes['ttSublabel'].currentValue === 'string' && changes['ttSublabel'].currentValue != changes['ttSublabel'].previousValue) {
            this.setSublabel(changes['ttSublabel'].currentValue);
        }

        if (changes['ttEditorCss']) {
            this.onCssChanged();
        }
    }

    // #region INSERT VIDEOLINK DIALOG

    /**
     * The icon used for insert videolink.
     */
    public videoIcon: SVGIcon = videoExternalIcon;

    /**
     * The default video width.
     */
    public defaultWidth: number = 500;

    /**
     * The default video height.
     */
    public defaultHeight: number = 340;

    /**
     * Video link for embedded videos (uses iframe).
     */
    public embeddedVideoUrl: string = '';

    /**
     * Video source url, (use standard video element).
     */
    public videoUrl: string = '';

    /**
     * Whether the dialog for inserting videolink is open.
     */
    public insertVideoOpened = false;

    /**
     * The last cursor position before opening video dialog.
     */
    private lastCursorPosition: number | undefined;

    /**
     * Closes the insert video link dialog.
     */
    public close(): void {
        this.insertVideoOpened = false;
    }

    /**
     * Opens the insert video link dialog.
     */
    public open(): void {
        const heading: Node[] = [];

        this.editor?.view.state.doc.content.forEach((node) => {
            if (node.type.name === 'heading') heading.push(node);
        });

        this.lastCursorPosition = this.editor?.view.state.selection.$anchor.pos;
        this.insertVideoOpened = true;
    }

    public insertVideo(): void {
        if (!!this.videoUrl) {
            const node = this.editor?.schema.nodes['video'].create({
                src: this.videoUrl,
                style: `width: ${this.defaultWidth}px;  max-width: 90%`,
            });

            if (this.lastCursorPosition && node) {
                const tr = this.editor?.view.state.tr.insert(this.lastCursorPosition, node);

                if (tr) {
                    this.editor?.view.dispatch(tr);
                    this.editor?.view.focus();
                }
            }
        } else if (!!this.embeddedVideoUrl) {
            this.embeddedVideoUrl = this.embeddedVideoUrl.substring(this.embeddedVideoUrl.indexOf('<iframe '), this.embeddedVideoUrl.indexOf('</iframe>')) + '</iframe>';

            const iframeWrapper = document.createElement('div');
            iframeWrapper.insertAdjacentHTML('afterbegin', this.embeddedVideoUrl);
            const iframe = iframeWrapper.firstElementChild;

            if (iframe?.tagName === 'IFRAME') {
                const attributes: any = {};
                let attribute: Attr | null = null;

                for (let i = 0; i < iframe.attributes.length; i++) {
                    attribute = iframe.attributes.item(i);

                    if (!!attribute && attribute.name !== 'style') {
                        attributes[attribute.name] = attribute.value;
                    }
                }

                attributes['style'] = `width: ${this.defaultWidth}px; max-width: 90%`;

                const node = this.editor?.schema.nodes['iframe'].create(attributes);

                if (this.lastCursorPosition && node) {
                    const tr = this.editor?.view.state.tr.insert(this.lastCursorPosition, node);

                    if (tr) {
                        this.editor?.view.dispatch(tr);
                        this.editor?.view.focus();
                    }
                }
            }
        }

        this.embeddedVideoUrl = '';
        this.videoUrl = '';
        this.defaultHeight = 340;
        this.defaultWidth = 500;

        this.close();
    }

    // #endregion INSERT VIDEOLINK DIALOG
}

import { Node } from '@progress/kendo-angular-editor';
import { DOMOutputSpec, NodeSpec } from '@progress/kendo-editor-common';

const video: NodeSpec = {
    // NodeSpec interface
    // http://prosemirror.net/docs/ref/#model.NodeSpec
    content: '',
    attrs: {
        crossorigin: { default: null },
        height: { default: null },
        width: { default: null },
        muted: { default: null },
        poster: { default: null },
        src: { default: null },
        style: { default: null },
    },
    group: 'block',
    selectable: true,
    parseDOM: [
        {
            tag: 'video',
            getAttrs: (dom: Element): any => ({
                crossorigin: dom.getAttribute('crossorigin'),
                height: dom.getAttribute('height'),
                width: dom.getAttribute('width'),
                muted: dom.getAttribute('muted'),
                poster: dom.getAttribute('poster'),
                src: dom.getAttribute('src'),
                style: dom.getAttribute('style'),
            }),
        },
    ],
    toDOM: (node: Node): DOMOutputSpec => {
        const attrs = {
            controls: true,
            crossorigin: node.attrs['crossorigin'],
            height: node.attrs['height'],
            width: node.attrs['width'],
            muted: node.attrs['muted'],
            poster: node.attrs['poster'],
            src: node.attrs['src'],
            style: node.attrs['style'],
        };
        return ['video', attrs];
    },
};
const iframe: NodeSpec = {
    // NodeSpec interface
    // http://prosemirror.net/docs/ref/#model.NodeSpec
    content: '',
    attrs: {
        allowfullscreen: { default: null },
        frameborder: { default: null },
        width: { default: null },
        height: { default: null },
        allow: { default: null },
        src: { default: null },
        title: { default: null },
        style: { default: null },
    },
    group: 'block',
    selectable: true,
    parseDOM: [
        {
            tag: 'iframe',
            getAttrs: (dom: Element): any => ({
                allowfullscreen: dom.getAttribute('allowfullscreen'),
                frameborder: dom.getAttribute('frameborder'),
                width: dom.getAttribute('width'),
                height: dom.getAttribute('height'),
                allow: dom.getAttribute('allow'),
                src: dom.getAttribute('src'),
                title: dom.getAttribute('title'),
                style: dom.getAttribute('style'),
            }),
        },
    ],
    toDOM: (node: Node): DOMOutputSpec => {
        const attrs = {
            allowfullscreen: node.attrs['allowfullscreen'],
            frameborder: node.attrs['frameborder'],
            width: node.attrs['width'],
            height: node.attrs['height'],
            allow: node.attrs['allow'],
            src: node.attrs['src'],
            title: node.attrs['title'],
            style: node.attrs['style'],
        };
        return ['iframe', attrs];
    },
};

export { video, iframe };

const tagMark = (tag: string): any => ({
    // MarkSpec interface
    // https://prosemirror.net/docs/ref/#model.MarkSpec
    [tag]: {
        name: tag,
        inclusive: true,
        parseDOM: [{ tag }],
        toDOM: () => [tag, 0],
    },
});

export { tagMark };

import { schema, Schema } from '@progress/kendo-angular-editor';
import { SVGIcon } from '@progress/kendo-angular-icons';
import { videoExternalIcon } from '@progress/kendo-svg-icons';

// Add the 'dir' attribute to paragraph node.
const paragraph = { ...schema.spec.nodes.get('paragraph') };

paragraph!.attrs!['dir'] = { default: null };
let nodes = schema.spec.nodes.update('paragraph', paragraph);

// Append the new node.
nodes = nodes.addToEnd('video', video);
nodes = nodes.addToEnd('iframe', iframe);

// Append a new mark representing the <s> formatting tag.
const mark = tagMark('s');
const marks = schema.spec.marks.append(mark);

// Create the new schema.
export const mySchema: Schema = new Schema({ nodes, marks });
